import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '@env/environment';

interface RecaptchaEnterpriseClient {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
}

interface RecaptchaWindow extends Window {
    grecaptcha?: {
        enterprise?: RecaptchaEnterpriseClient;
    };
}

@Injectable({
    providedIn: 'root'
})
export class RecaptchaService {
    private readonly document = inject(DOCUMENT);
    private readonly platformId = inject(PLATFORM_ID);
    private scriptLoader: Promise<void> | null = null;

    async getToken(action: string): Promise<string | null> {
        if (!isPlatformBrowser(this.platformId)) {
            return null;
        }

        const siteKey = environment.recaptcha.siteKey?.trim();
        if (!siteKey) {
            throw new Error('Falta la site key de reCAPTCHA Enterprise.');
        }

        await this.loadRecaptchaScript(siteKey);

        const recaptchaClient = this.getRecaptchaClient();
        await new Promise<void>((resolve) => {
            recaptchaClient.ready(() => resolve());
        });

        return recaptchaClient.execute(siteKey, { action });
    }

    private loadRecaptchaScript(siteKey: string): Promise<void> {
        const currentWindow = this.document.defaultView as RecaptchaWindow | null;
        if (currentWindow?.grecaptcha?.enterprise) {
            return Promise.resolve();
        }

        if (this.scriptLoader) {
            return this.scriptLoader;
        }

        this.scriptLoader = new Promise<void>((resolve, reject) => {
            const existingScript = this.document.getElementById('google-recaptcha-enterprise');
            if (existingScript) {
                existingScript.addEventListener('load', () => resolve(), { once: true });
                existingScript.addEventListener(
                    'error',
                    () => {
                        this.scriptLoader = null;
                        reject(new Error('No se pudo cargar reCAPTCHA Enterprise.'));
                    },
                    { once: true }
                );
                return;
            }

            const script = this.document.createElement('script');
            script.id = 'google-recaptcha-enterprise';
            script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(siteKey)}`;
            script.async = true;
            script.defer = true;
            script.onload = () => resolve();
            script.onerror = () => {
                this.scriptLoader = null;
                reject(new Error('No se pudo cargar reCAPTCHA Enterprise.'));
            };

            const target = this.document.head ?? this.document.body ?? this.document.documentElement;
            target.appendChild(script);
        });

        return this.scriptLoader;
    }

    private getRecaptchaClient(): RecaptchaEnterpriseClient {
        const currentWindow = this.document.defaultView as RecaptchaWindow | null;
        if (!currentWindow?.grecaptcha?.enterprise) {
            throw new Error('reCAPTCHA Enterprise no está disponible en el navegador.');
        }

        return currentWindow.grecaptcha.enterprise;
    }
}
